import React, { useState, useEffect } from 'react';
import { Plus, Search, FileText, ChevronDown, Pencil, Trash2 } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Proposal, deleteProposal, getProposal, getProposalEvents } from '../lib/proposals';
import { useProposals } from '../hooks/useProposals';
import { useBusiness } from '../contexts/BusinessContext';
import EmptyState from '../components/EmptyState';
import CreateProposalModal from '../components/CreateProposalModal';
import ProposalDetailsModal from '../components/ProposalDetailsModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import PermissionGuard from '../components/PermissionGuard';
import { ProposalSkeletonList } from '../components/ProposalSkeleton';

const statusOptions = [
  { label: 'All Proposals', value: 'all' },
  { label: 'Draft', value: 'draft' },
  { label: 'Sent', value: 'sent' },
  { label: 'Viewed', value: 'viewed' },
  { label: 'Approved', value: 'approved' },
  { label: 'Declined', value: 'declined' }
];

interface ProposalsProps {
  readOnly?: boolean;
}

function Proposals({ readOnly = false }: ProposalsProps) {
  const { selectedBusiness } = useBusiness();
  const location = useLocation();
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(statusOptions[0]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState<Proposal | null>(null);
  const { proposals, loading, loadingMore, error, hasMore, totalCount, refetch, loadMore } = useProposals(selectedBusiness?.id);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [proposalToDelete, setProposalToDelete] = useState<Proposal | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [proposalEvents, setProposalEvents] = useState<Record<string, any[]>>({});
  const navigate = useNavigate();

  // Helper function to check if proposal is expired
  const isProposalExpired = (proposal: Proposal) => {
    if (!proposal.valid_until) return false;
    return new Date(proposal.valid_until) < new Date();
  };

  // Helper function to get display status
  const getDisplayStatus = (proposal: Proposal) => {
    if (proposal.status === 'approved') return proposal.status;
    if (isProposalExpired(proposal)) return 'expired';
    return proposal.status;
  };

  // Helper function to get status timestamp
  const getStatusTimestamp = (proposal: Proposal, status: string) => {
    const events = proposalEvents[proposal.id] || [];
    
    // For expired status, use the valid_until date
    if (status === 'expired' && proposal.valid_until) {
      return new Date(proposal.valid_until).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
    
    // Look for status change events
    const statusEvent = events.find(event => 
      event.event_type === 'status_changed' && 
      event.metadata?.new_status === status
    );
    
    if (statusEvent) {
      return new Date(statusEvent.event_timestamp).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
    
    // For created status, use created_at
    if (status === 'draft') {
      return new Date(proposal.created_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
    
    return null;
  };

  // Load proposal events when proposals change
  useEffect(() => {
    const loadProposalEvents = async () => {
      if (!proposals.length) return;
      
      const eventsMap: Record<string, any[]> = {};
      
      // Load events for all proposals
      await Promise.all(
        proposals.map(async (proposal) => {
          try {
            const events = await getProposalEvents(proposal.id);
            eventsMap[proposal.id] = events;
          } catch (error) {
            console.error(`Error loading events for proposal ${proposal.id}:`, error);
            eventsMap[proposal.id] = [];
          }
        })
      );
      
      setProposalEvents(eventsMap);
    };
    
    loadProposalEvents();
  }, [proposals]);

  const filteredProposals = proposals.filter((proposal) => {
    if (selectedStatus.value !== 'all' && proposal.status !== selectedStatus.value) {
      return false;
    }

    const searchLower = searchQuery.toLowerCase();
    return (
      proposal.title.toLowerCase().includes(searchLower) ||
      proposal.customer?.first_name?.toLowerCase().includes(searchLower) ||
      proposal.customer?.last_name?.toLowerCase().includes(searchLower) ||
      proposal.customer?.email?.toLowerCase().includes(searchLower)
    );
  });

  // Handle navigation from customer details modal with proposal ID
  useEffect(() => {
    const handleProposalNavigation = async () => {
      const state = location.state as { selectedProposalId?: string } | null;
      if (state?.selectedProposalId && selectedBusiness?.id) {
        try {
          // Fetch the specific proposal details
          const proposal = await getProposal(state.selectedProposalId);
          setSelectedProposal(proposal);
          setIsDetailsModalOpen(true);
          
          // Clear the navigation state to prevent reopening on refresh
          window.history.replaceState({}, document.title);
        } catch (err) {
          console.error('Error fetching proposal from navigation:', err);
        }
      }
    };

    handleProposalNavigation();
  }, [location.state, selectedBusiness?.id]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-700';
      case 'sent':
        return 'bg-blue-100 text-blue-700';
      case 'viewed':
        return 'bg-purple-100 text-purple-700';
      case 'approved':
        return 'bg-green-100 text-green-700';
      case 'declined':
      case 'rejected':
        return 'bg-red-100 text-red-700';
      case 'expired':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };


  return (
    <div 
      className="flex flex-col h-full overflow-y-auto bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen" 
      data-proposals-page
      style={{ 
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        margin: 0,
        padding: 0
      }}>

      {/* Filters and Search Section */}
      <div className="bg-white/90 backdrop-blur-xl border-b border-gray-200/30 px-4 md:px-8 py-4 sticky top-0 z-30" style={{
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)'
      }}>
        <div className="flex flex-col space-y-4 md:space-y-0 md:flex-row md:items-center md:justify-between">
          {/* Status Filter - Full width on mobile, first row */}
          <div className="relative w-full md:w-auto">
            <button
              onClick={() => setIsStatusOpen(!isStatusOpen)}
              className="w-full md:w-auto inline-flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-900 bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl hover:bg-white/90 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
              style={{ minHeight: '44px' }}
            >
              <span>{selectedStatus.label}</span>
              <ChevronDown
                className={`ml-2 h-5 w-5 transition-transform duration-300 ${
                  isStatusOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            {isStatusOpen && (
              <div className="absolute left-0 mt-2 w-full sm:w-56 rounded-2xl shadow-2xl bg-white/95 backdrop-blur-xl border border-gray-200/50 z-50 transform transition-all duration-300 ease-out animate-in slide-in-from-top-2 fade-in-0" style={{
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)'
              }}>
                <div className="py-2" role="menu" aria-orientation="vertical">
                  {statusOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setSelectedStatus(option);
                        setIsStatusOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 text-sm transition-all duration-200 ease-out touch-manipulation ${
                        selectedStatus.value === option.value
                          ? 'bg-blue-50 text-blue-900 font-semibold'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                      role="menuitem"
                      style={{ minHeight: '44px' }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Search Bar and New Proposal Button - Same line on mobile, second row */}
          <div className="flex flex-row items-center space-x-3 md:space-x-4 w-full md:w-auto">
            {/* Search Bar */}
            <div className="relative flex-1 md:flex-none">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search proposals"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full md:w-64"
              />
            </div>

            {/* New Proposal Button */}
            {!readOnly && (
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="flex-shrink-0 inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
                style={{ minHeight: '40px' }}
              >
                <Plus className="h-4 w-4 mr-1" />
                New
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="flex-1">
        {/* Mobile List View */}
        <div className="sm:hidden px-4 py-6">
          {loading ? (
            <ProposalSkeletonList count={8} isMobile={true} />
          ) : filteredProposals.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <EmptyState
                icon={<FileText className="w-8 h-8 text-gray-400" />}
                title={searchQuery ? 'No matching proposals' : 'No proposals yet'}
                description={
                  searchQuery
                    ? 'Try adjusting your search terms'
                    : 'Start creating proposals to send to your customers.'
                }
                actionLabel={
                  searchQuery ? 'Clear search' : 'Create Proposal'
                }
                onAction={() => (searchQuery ? setSearchQuery('') : setIsCreateModalOpen(true))}
              />
            </div>
          ) : (
            <div className="space-y-4">
              {filteredProposals.map((proposal, index) => {
                const displayStatus = getDisplayStatus(proposal);
                const isExpired = displayStatus === 'expired';
                
                return (
                  <div
                    key={proposal.id}
                    className={`bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 ease-out transform hover:scale-[1.02] active:scale-[0.98] cursor-pointer flex ${
                      isExpired ? 'opacity-60' : ''
                    }`}
                    onClick={() => {
                      setSelectedProposal(proposal);
                      setIsDetailsModalOpen(true);
                    }}
                    style={{
                      animationDelay: `${index * 50}ms`
                    }}
                  >
                    {/* Status Bar - Left Edge */}
                    <div className={`w-1 rounded-l-xl ${
                      displayStatus === 'approved' ? 'bg-green-500' :
                      displayStatus === 'sent' ? 'bg-blue-500' :
                      displayStatus === 'rejected' || displayStatus === 'declined' ? 'bg-red-500' :
                      displayStatus === 'expired' ? 'bg-gray-500' :
                      displayStatus === 'draft' ? 'bg-gray-400' :
                      'bg-gray-300'
                    }`} />
                    
                    <div className="p-4 flex-1">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center text-xs font-bold text-white">
                            {proposal.customer?.first_name?.[0]}{proposal.customer?.last_name?.[0]}
                          </div>
                          <div>
                            <h3 className="text-sm font-semibold text-gray-900 truncate">
                              {proposal.customer?.first_name} {proposal.customer?.last_name}
                            </h3>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            displayStatus === 'approved' ? 'bg-green-100 text-green-700' :
                            displayStatus === 'sent' ? 'bg-blue-100 text-blue-700' :
                            displayStatus === 'rejected' || displayStatus === 'declined' ? 'bg-red-100 text-red-700' :
                            displayStatus === 'expired' ? 'bg-gray-100 text-gray-700' :
                            displayStatus === 'draft' ? 'bg-gray-100 text-gray-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
                          </span>
                          {getStatusTimestamp(proposal, displayStatus) && (
                            <p className="text-xs text-gray-500 mt-1">
                              {getStatusTimestamp(proposal, displayStatus)}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Title */}
                      <h2 className="text-base font-semibold text-gray-900 mb-3 line-clamp-2">
                        {proposal.title}
                      </h2>

                      {/* Amount and Valid Until */}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xl font-bold text-gray-900">
                            ${proposal.total_amount.toLocaleString()}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">
                            Valid until
                          </p>
                          <p className="text-sm font-medium text-gray-900">
                            {proposal.valid_until ? new Date(proposal.valid_until).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            }) : 'No expiry'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Desktop Grid View */}
        <div className="hidden sm:block px-4 md:px-8 py-6">
          {loading ? (
            <ProposalSkeletonList count={10} isMobile={false} />
          ) : filteredProposals.length === 0 ? (
            <div className="flex items-center justify-center h-64 bg-white/90 backdrop-blur-xl rounded-2xl border border-gray-200/50 shadow-lg" style={{
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)'
            }}>
              <EmptyState
                icon={<FileText className="w-8 h-8 text-gray-400" />}
                title={searchQuery ? 'No matching proposals' : 'No proposals yet'}
                description={
                  searchQuery
                    ? 'Try adjusting your search terms'
                    : 'Start creating proposals to send to your customers.'
                }
                actionLabel={
                  searchQuery ? 'Clear search' : 'Create Proposal'
                }
                onAction={() => (searchQuery ? setSearchQuery('') : setIsCreateModalOpen(true))}
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredProposals.map((proposal, index) => {
                const displayStatus = getDisplayStatus(proposal);
                const isExpired = displayStatus === 'expired';
                
                return (
                  <div
                    key={proposal.id}
                    className={`bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 ease-out transform hover:scale-[1.02] active:scale-[0.98] cursor-pointer flex ${
                      isExpired ? 'opacity-60' : ''
                    }`}
                    onClick={() => {
                      setSelectedProposal(proposal);
                      setIsDetailsModalOpen(true);
                    }}
                    style={{
                      animationDelay: `${index * 50}ms`
                    }}
                  >
                  {/* Status Bar - Left Edge */}
                  <div className={`w-1 rounded-l-xl ${
                    displayStatus === 'approved' ? 'bg-green-500' :
                    displayStatus === 'sent' ? 'bg-blue-500' :
                    displayStatus === 'rejected' || displayStatus === 'declined' ? 'bg-red-500' :
                    displayStatus === 'expired' ? 'bg-gray-500' :
                    displayStatus === 'draft' ? 'bg-gray-400' :
                    'bg-gray-300'
                  }`} />
                  
                  <div className="p-6 flex-1">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-sm font-bold text-white">
                          {proposal.customer?.first_name?.[0]}{proposal.customer?.last_name?.[0]}
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900 truncate">
                            {proposal.customer?.first_name} {proposal.customer?.last_name}
                          </h3>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          displayStatus === 'approved' ? 'bg-green-100 text-green-700' :
                          displayStatus === 'sent' ? 'bg-blue-100 text-blue-700' :
                          displayStatus === 'rejected' || displayStatus === 'declined' ? 'bg-red-100 text-red-700' :
                          displayStatus === 'expired' ? 'bg-gray-100 text-gray-700' :
                          displayStatus === 'draft' ? 'bg-gray-100 text-gray-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
                        </span>
                        {getStatusTimestamp(proposal, displayStatus) && (
                          <p className="text-xs text-gray-500 mt-1">
                            {getStatusTimestamp(proposal, displayStatus)}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Title */}
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 line-clamp-2">
                      {proposal.title}
                    </h2>

                    {/* Amount */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-2xl font-bold text-gray-900">
                          ${proposal.total_amount.toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">
                          Valid until
                        </p>
                        <p className="text-sm font-medium text-gray-900">
                          {proposal.valid_until ? new Date(proposal.valid_until).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          }) : 'No expiry'}
                        </p>
                      </div>
                    </div>
                  </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Load More Button */}
        {hasMore && filteredProposals.length > 0 && (
          <div className="mt-6 flex justify-center">
            <button
              onClick={loadMore}
              disabled={loadingMore}
              className="inline-flex items-center px-8 py-4 text-base font-semibold text-white bg-purple-700 hover:bg-purple-600 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
              style={{ minHeight: '44px' }}
            >
              {loadingMore ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                  Loading...
                </>
              ) : (
                'Load More'
              )}
            </button>
          </div>
        )}
      </div>

      <CreateProposalModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={(createdProposal) => {
          setIsCreateModalOpen(false);
          refetch();
          
          // Show the details modal for the newly created proposal
          setSelectedProposal(createdProposal);
          setIsDetailsModalOpen(true);
        }}
      />

      <ProposalDetailsModal
        isOpen={isDetailsModalOpen && selectedProposal !== null}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setSelectedProposal(null);
        }}
        proposal={selectedProposal!}
        onUpdate={refetch}
        readOnly={readOnly}
        onDelete={refetch}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setProposalToDelete(null);
        }}
        onConfirm={async () => {
          if (!proposalToDelete) return;
          try {
            setDeleteLoading(true);
            await deleteProposal(proposalToDelete.id);
            await refetch();
            setIsDeleteModalOpen(false);
            setProposalToDelete(null);
          } catch (err) {
            console.error('Error deleting proposal:', err);
            // Error state is now managed by the hook
          } finally {
            setDeleteLoading(false);
          }
        }}
        title="Delete Proposal"
        message={`Are you sure you want to delete this proposal? This action cannot be undone.`}
        loading={deleteLoading}
      />
    </div>
  );
}

// Wrap with PermissionGuard
function ProposalsWithPermission({ readOnly = false }: ProposalsProps) {
  return (
    <PermissionGuard permission="proposals.pageAccess">
      <Proposals readOnly={readOnly} />
    </PermissionGuard>
  );
}

export default ProposalsWithPermission;